import { Dialog } from '@spiaggeit/spit-ui'
import { decode } from 'html-entities'
import { ComponentPropsWithoutRef } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { useAppSelector } from '@/hooks/store.ts'
import { appSlice } from '@/store/appSlice.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { selectBookingPolicyString } from '@/store/licenseSlice/selectors/selectBookingPolicyString.ts'
import {
  REFUND_POLICY,
  REFUND_POLICY_DAYS_MAP,
} from '@/utils/bookingRefundPolicy.ts'
import { DialogProps } from '../../models/dialog-props.ts'

export interface RefundPolicyDialogProps extends DialogProps {
  bookingRefundPolicy?: number
  licenseName: string
}

export const RefundPolicyDialog = (props: RefundPolicyDialogProps) => {
  const { bookingRefundPolicy: days = 0, licenseName, ...dialogProps } = props
  const isKiosk = useAppSelector(appSlice.selectors.isKiosk)
  const { t } = useTranslation()
  const mappedDays = REFUND_POLICY_DAYS_MAP[days]
  const paragraphs = [
    {
      description: t('bookingConditions.advancePaymentDescription'),
      title: t('bookingConditions.advancePaymentTitle'),
    },
    {
      description: decode(
        t('bookingConditions.refundableByVoucherDescription', {
          days: mappedDays,
          licenseName,
        })
      ),
      title: t('bookingConditions.refundableByVoucherTitle'),
    },
    {
      description: t('bookingConditions.refundTimetableDescription', {
        days: mappedDays,
      }),
      title: t('bookingConditions.refundTimetableTitle'),
    },
  ]

  const neverParagraphs = [
    {
      description: t('bookingConditions.advancePaymentDescription'),
      title: t('bookingConditions.advancePaymentTitle'),
    },
    {
      description: t('bookingConditions.advancePaymentDescriptionNever'),
      title: t('bookingConditions.refundableByVoucherTitleNever'),
    },
    {
      description: t('bookingConditions.refundTimetableDescriptionNever'),
      title: t('bookingConditions.refundTimetableTitleNever'),
    },
  ]

  const dayBeforeParagraphs = [
    {
      description: t('bookingConditions.advancePaymentDescription'),
      title: t('bookingConditions.advancePaymentTitle'),
    },
    {
      description: t(
        'bookingConditions.refundableByVoucherDescriptionDayBefore',
        {
          licenseName,
        }
      ),
      title: t('bookingConditions.refundableByVoucherTitle'),
    },
    {
      description: t('bookingConditions.refundTimetableDescriptionDayBefore'),
      title: t('bookingConditions.refundTimetableTitle'),
    },
  ]

  const visibleParagraphs = (() => {
    if (mappedDays === REFUND_POLICY_DAYS_MAP[REFUND_POLICY.NEVER]) {
      return neverParagraphs
    } else if (
      mappedDays === REFUND_POLICY_DAYS_MAP[REFUND_POLICY.DAY_BEFORE]
    ) {
      return dayBeforeParagraphs
    } else {
      return paragraphs
    }
  })()

  const bookingPolicyText = useAppSelector(selectBookingPolicyString)

  const isLoadingStrings = useAppSelector(
    licenseSlice.selectors.isLoadingLicenseStrings
  )

  if (isLoadingStrings) {
    return (
      <>
        <LoaderTrigger />
        <p>{t('bookingConditions.title')}</p>
      </>
    )
  }

  return (
    <Dialog
      scrollContent
      size={isKiosk ? null : 'sm'}
      title={t('bookingConditions.title')}
      {...dialogProps}
    >
      {bookingPolicyText ? (
        <Content
          dangerouslySetInnerHTML={{
            __html: bookingPolicyText.replace(/\n/g, '<br />'),
          }}
        />
      ) : (
        <Content>
          {visibleParagraphs.map((paragraph) => (
            <div className="pb-4" key={paragraph.title}>
              <h3 className="font-bold text-primary">{paragraph.title}</h3>
              <p className="text-secondary">{paragraph.description}</p>
            </div>
          ))}
        </Content>
      )}
    </Dialog>
  )
}

const Content = (props: ComponentPropsWithoutRef<'div'>) => {
  const { children, ...rest } = props

  return (
    <div className="p-4" {...rest}>
      {children}
    </div>
  )
}
